import { AppSettingsState } from 'app/store/appSettings/AppSettingsState';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import State from 'store/State';
import { ChannelDestination } from 'communication/Channel';
import { StreamDataHandlers } from 'streamsState/StreamDataService';
import { WidgetClearStreamDataAction } from 'widgets/store/stream/actions';
import { Store } from 'redux';
import { LoggingService } from '@instinet-analytics/core-lib';
import { RpcRequestHandlers } from 'widgets/store/rpcStream/handlers';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import { MarketDataRequest } from 'marketData/MarketDataRequest';

/*
 * Portfolio Selection dependency types that streams can depend on
 */
export enum PortfolioSelectionDependencyType {
    SingleSymbolQuantity,
    AtLeastOneSymbolQuantity,
    SingleSymbol,
}

export interface StreamingStreamConfig<TDataMessage> {
    streamType: 'StreamingStream';
    selectWidgetStreamKey: (
        state: State,
        widgetKey: string
    ) => string | undefined;
    buildHandlers?: (
        widgetKey: string,
        store: Store,
        loggingService: LoggingService
    ) => StreamDataHandlers<TDataMessage>;
}

export interface StreamingStreamByRequestUidConfig {
    streamType: 'StreamingStreamByRequestUid';
    buildRequestUid: (state: State, widgetKey: string) => string | undefined;
}

export interface RpcStreamConfig<TResponse> {
    streamType: 'RpcStream';
    buildHandlers: (
        widgetKey: string,
        store: Store,
        loggingService: LoggingService
    ) => RpcRequestHandlers<TResponse>;
}

export type StreamTypeConfig<T> =
    | StreamingStreamConfig<T>
    | RpcStreamConfig<T>
    | StreamingStreamByRequestUidConfig;

export interface OrderIdsDependentConfig<TPayload> {
    configType: 'OrderIdsDependentWidgetStreamConfig';

    createPayload: (
        orderIds: string[],
        portfolioSelection: PortfolioSelectionWrapper,
        widgetKey: string,
        appSettings: AppSettingsState,
        state: State
    ) => TPayload | undefined;

    createMarketDataRequests?: (
        portfolioSelection: PortfolioSelectionWrapper,
        widgetKey: string,
        appSettings: AppSettingsState,
        state: State
    ) => MarketDataRequest[];

    /*
     * True if the stream falls back to the portfolio selection when there are no orders
     */
    fallsBackToPortfolioSelection?: boolean;
}

export interface NotDependentOnPortfolioConfig<TPayload> {
    configType: 'NotDependentOnPortfolioWidgetStreamConfig';
    createPayload: (
        widgetKey: string,
        appSettings: AppSettingsState,
        state: State
    ) => TPayload | undefined;
}

export interface PortfolioSelectionDependentConfig<TPayload> {
    configType: 'PortfolioSelectionDependentWidgetStreamConfig';
    createPayload: (
        portfolioSelection: PortfolioSelectionWrapper,
        widgetKey: string,
        appSettings: AppSettingsState,
        state: State
    ) => TPayload | undefined;
    createMarketDataRequests?: (
        portfolioSelection: PortfolioSelectionWrapper,
        widgetKey: string,
        appSettings: AppSettingsState,
        state: State
    ) => MarketDataRequest[];
    selectionDependencyType: PortfolioSelectionDependencyType;
    onStartStreaming?: (
        state: State,
        dispatcher: ActionsDispatcher,
        payload: unknown,
        widgetKey: string
    ) => void;
}

export interface FilteredPortfolioDependentWidgetConfig<TPayload> {
    configType: 'FilteredPortfolioDependentWidgetStreamConfig';
    createPayload: (
        widgetKey: string,
        appSettings: AppSettingsState,
        filteredPortfolio: PortfolioItem[],
        state: State
    ) => TPayload | undefined;
    onStartStreaming?: (
        state: State,
        dispatcher: ActionsDispatcher,
        payload: unknown,
        widgetKey: string,
        filteredPortfolio: PortfolioItem[]
    ) => void;
}

export type WidgetStreamDependencyConfig<TPayload> =
    | NotDependentOnPortfolioConfig<TPayload>
    | PortfolioSelectionDependentConfig<TPayload>
    | FilteredPortfolioDependentWidgetConfig<TPayload>
    | OrderIdsDependentConfig<TPayload>;

export interface WidgetStreamConfigBase {
    startChannelDestination: ChannelDestination;

    clearWidgetStreamData: (widgetKey: string) => WidgetClearStreamDataAction;

    allowMultiStream?: boolean;
    subscriptions?: {
        appSettings?: boolean;
        widgetConfig?: boolean;
        widgetComponentState?: boolean;
    };
    throttleRequestsMs?: number;
}

export type WidgetStreamStreamingConfig<TPayload, TDataMessage> =
    WidgetStreamDependencyConfig<TPayload> &
        StreamingStreamConfig<TDataMessage> &
        WidgetStreamConfigBase;

export type WidgetStreamRpcConfig<TPayload, TResponse> =
    WidgetStreamDependencyConfig<TPayload> &
        RpcStreamConfig<TResponse> &
        WidgetStreamConfigBase;

export type WidgetStreamConfig<TPayload, TDataMessageOrResponse> =
    WidgetStreamDependencyConfig<TPayload> &
        StreamTypeConfig<TDataMessageOrResponse> &
        WidgetStreamConfigBase;
